import { useAuth } from './useAuth';
import { useCurrentUser } from './useCurrentUser';

export interface UserPermissions {
  // Content creation permissions
  canCreatePosts: boolean;
  canComment: boolean;
  canUpvote: boolean;
  canSavePosts: boolean;
  
  // Administrative permissions
  canAccessAdminPanel: boolean;
  canViewAnalytics: boolean;
  canManageUsers: boolean;
  
  // Role information
  userRole: 'surgeon' | 'staff' | 'unknown';
  isStaff: boolean;
  isSurgeon: boolean;
}

/**
 * Hook to determine user permissions based on role
 * Matches the mobile app's permission system
 */
export function usePermissions(): UserPermissions {
  const { user } = useAuth();
  const { data: currentUser, isLoading: isUserLoading } = useCurrentUser();
  
  // Wait for user data to load before determining permissions
  // If we're still loading user data, default to no permissions for safety
  if (isUserLoading && !user?.userType) {
    return {
      canCreatePosts: false,
      canComment: false,
      canUpvote: true,
      canSavePosts: true,
      canAccessAdminPanel: false,
      canViewAnalytics: false,
      canManageUsers: false,
      userRole: 'unknown',
      isStaff: false,
      isSurgeon: false,
    };
  }

  const userType = user?.userType || currentUser?.userType || 'unknown';

  const isSurgeon = userType === 'surgeon';
  const isStaff = currentUser?.isStaff === true || userType === 'staff';
  // Admin features gate on the explicit isAdmin flag from the server,
  // not on userType/email — staff and @exac.com employees are NOT admins by default.
  const isAdmin = currentUser?.isAdmin === true || user?.isAdmin === true;

  return {
    canCreatePosts: isSurgeon,
    canComment: isSurgeon,
    canUpvote: isSurgeon,
    canSavePosts: true,

    canAccessAdminPanel: isAdmin,
    canViewAnalytics: isAdmin,
    canManageUsers: isAdmin,

    userRole: isSurgeon ? 'surgeon' : (isStaff ? 'staff' : 'unknown'),
    isStaff,
    isSurgeon,
  };
}

/**
 * Hook for checking specific permissions
 */
export function usePermissionCheck() {
  const permissions = usePermissions();
  
  return {
    ...permissions,
    
    // Helper functions for permission checks
    requireSurgeon: (action: string = 'perform this action') => {
      if (!permissions.isSurgeon) {
        throw new Error(`Only surgeons can ${action}`);
      }
    },
    
    requireStaff: (action: string = 'access this feature') => {
      if (!permissions.isStaff) {
        throw new Error(`Only staff members can ${action}`);
      }
    },
    
    canPerform: (permission: keyof UserPermissions) => {
      return permissions[permission] === true;
    },
  };
}